import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Download, Settings2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Avatar } from "@/components/Avatar";
import { API, api, errMsg } from "@/lib/api";
import { analytics } from "@/lib/analytics";
import { fmtRelative } from "@/lib/format";
import { RangeSelect, dash, downloadFile, mins, num, useRange } from "./shared";
import { PerformanceMember } from "./PerformanceMember";

export const COLUMNS = [
  ["messages", "Messages", num, "Top-level messages posted"],
  ["replies", "Replies", num, "Thread replies posted"],
  ["reactions_given", "Reactions given", num],
  ["reactions_received", "Reactions received", num, "Reactions on this member's messages"],
  ["mentions_answered", "Mentions answered", (v, r) => `${num(v)} / ${num(r.mentions_received)}`, "Answered within 24h / received"],
  ["dms_answered", "DMs answered", (v, r) => `${num(v)} / ${num(r.dms_received)}`, "Answered within 24h / received"],
  ["response_median_min", "Median response", (v) => dash(v, mins), "Median time to first reply to a DM or mention"],
  ["active_minutes", "Active time", mins, "Minutes with mouse/keyboard activity"],
  ["reading_minutes", "Reading time", mins, "Minutes with a conversation visible"],
  ["messages_read", "Msgs read", num, "Messages scrolled into view"],
  ["keystrokes", "Keystrokes", num],
  ["chars_typed", "Chars typed", num],
  ["backspaces", "Backspaces", num],
  ["drafts_abandoned", "Drafts dropped", num, "Typed but never sent"],
  ["avg_compose_sec", "Avg compose", (v) => dash(v, (s) => `${s}s`), "Average seconds from first keystroke to send"],
  ["clicks", "Clicks", num],
  ["events", "Events", num, "All tracked events"],
  ["sessions", "Sessions", num],
  ["files", "Files", num],
  ["huddles", "Huddles", num],
  ["huddle_minutes", "Huddle time", mins],
  ["channels_active", "Channels", num, "Conversations posted in"],
  ["searches", "Searches", num],
  ["logins", "Logins", num],
  ["last_active", "Last active", (v) => (v ? fmtRelative(v) : "—")],
];

export const SLA_OPTIONS = [5, 15, 30, 60, 240];

export const TrackingSettings = () => {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/tracking").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const toggle = async (k, v) => {
    try { const { data } = await api.patch("/admin/tracking", { [k]: v }); setCfg(data); analytics.setTracking(data); toast.success(`${LABELS[k][0]} ${v ? "enabled" : "paused"}`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="tc-popwrap">
      <button className={`tc-btn tc-btn--outline sm${open ? " is-on" : ""}`} onClick={() => setOpen((o) => !o)} data-testid="tracking-settings-button"><Settings2 size={14} /> Capture settings</button>
      {open && cfg && (
        <div className="tc-pop tc-fade-in" data-testid="tracking-settings-panel">
          <div className="tc-pop__title">What gets captured</div>
          {Object.entries(LABELS).map(([k, [label, desc]]) => (
            <label key={k} className="tc-pop__row" data-testid={`tracking-toggle-${k}`}>
              <div><div className="tc-pop__label">{label}</div><div className="tc-pop__desc">{desc}</div></div>
              <Switch checked={!!cfg[k]} onCheckedChange={(v) => toggle(k, v)} data-testid={`tracking-switch-${k}`} />
            </label>
          ))}
          <div className="tc-pop__foot">Clicks, messages, reactions, threads, huddles, files and logins are always captured.</div>
        </div>
      )}
    </div>
  );
};

const LABELS = {
  keystrokes: ["Every keystroke", "One event per key press in the composer (key, class, caret)"],
  snapshots: ["Draft snapshots", "Text of unsent drafts, every 8s while typing and when abandoned"],
  heartbeats: ["Active-time heartbeats", "30-second heartbeat while the member is active; powers active minutes"],
  reading: ["Reading & attention", "Messages scrolled into view, time per conversation, scroll depth"],
};

export const Performance = () => {
  const [sp] = useSearchParams();
  const member = sp.get("member");
  return member ? <PerformanceMember uid={member} /> : <Leaderboard />;
};

const Leaderboard = () => {
  const { range, setRange, params } = useRange("30d");
  const [sort, setSort] = useState("messages");
  const [order, setOrder] = useState("desc");
  const [sla, setSla] = useState(15);
  const [rows, setRows] = useState(null);
  const [cols, setCols] = useState(() => { try { return JSON.parse(localStorage.getItem("tc_perf_cols")) || DEFAULT_COLS; } catch { return DEFAULT_COLS; } });
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/admin/performance/members", { params: { ...params, sort, order, sla_minutes: sla } }).then((r) => setRows(r.data.members)).catch((e) => toast.error(errMsg(e)));
  }, [params, sort, order, sla]);
  const clickSort = (k) => {
    if (sort === k) setOrder((o) => (o === "desc" ? "asc" : "desc")); else { setSort(k); setOrder("desc"); }
    analytics.track("performance_sorted", { category: "reporting", label: k });
  };
  const toggleCol = (k) => setCols((c) => { const n = c.includes(k) ? c.filter((x) => x !== k) : [...c, k]; localStorage.setItem("tc_perf_cols", JSON.stringify(n)); return n; });
  const visible = COLUMNS.filter(([k]) => cols.includes(k));
  const qs = new URLSearchParams({ ...params, sort, order, sla_minutes: sla }).toString();
  return (
    <div data-testid="admin-performance">
      <h1>Team performance</h1>
      <p className="lead">Every click, keystroke, message, reply, reaction and response time — per member. Click a column to rank by it, click a member to drill into their timeline.</p>
      <div className="tc-admin__toolbar">
        <RangeSelect range={range} setRange={setRange} />
        <select className="tc-select" value={sla} onChange={(e) => setSla(Number(e.target.value))} data-testid="sla-select" title="Response SLA">
          {SLA_OPTIONS.map((m) => <option key={m} value={m}>{`SLA ${mins(m)}`}</option>)}
        </select>
        <ColumnPicker cols={cols} toggle={toggleCol} />
        <span className="grow" />
        <TrackingSettings />
        <button className="tc-btn tc-btn--outline sm" onClick={() => downloadFile(`${API}/admin/performance/members?${qs}&format=csv`, `total-chat-performance-${dayjs().format("YYYYMMDD")}.csv`, "CSV")} data-testid="performance-export-csv"><Download size={14} /> CSV</button>
        <button className="tc-btn tc-btn--outline sm" onClick={() => downloadFile(`${API}/admin/analytics/export.ndjson?${new URLSearchParams(params).toString()}&limit=200000`, `total-chat-events-${dayjs().format("YYYYMMDD")}.ndjson`, "NDJSON")} data-testid="performance-export-ndjson"><Download size={14} /> Raw events</button>
      </div>
      <div className="tc-admin-card" style={{ overflowX: "auto" }}>
        <table className="tc-table tc-table--perf" data-testid="performance-table">
          <thead>
            <tr>
              <th className="num">#</th><th>Member</th>
              {visible.map(([k, label, , tip]) => (
                <th key={k} className={`num sortable${sort === k ? " is-sorted" : ""}`} title={tip || label} onClick={() => clickSort(k)} data-testid={`perf-col-${k}`}>
                  {label} {sort === k ? (order === "desc" ? <ArrowDown size={11} /> : <ArrowUp size={11} />) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.user.id} className="clickable" onClick={() => { analytics.track("performance_member_opened", { category: "reporting", target: "user", target_id: r.user.id }); navigate(`/admin/performance?member=${r.user.id}`); }} data-testid={`perf-row-${r.user.id}`}>
                <td className="num muted">{r.rank}</td>
                <td><div className="usercell"><Avatar user={r.user} size={26} radius={5} /><div><div>{r.user.display_name}</div><div className="sub">{r.user.title || `@${r.user.handle}`}</div></div></div></td>
                {visible.map(([k, , fmt]) => <td key={k} className={`num${sort === k ? " is-sorted" : ""}`}>{fmt(r[k], r)}</td>)}
              </tr>
            ))}
            {rows && !rows.length && <tr><td colSpan={visible.length + 2} className="muted" style={{ textAlign: "center", padding: 32 }}>No members yet.</td></tr>}
            {!rows && <tr><td colSpan={visible.length + 2} className="muted" style={{ textAlign: "center", padding: 32 }}>Crunching numbers…</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const DEFAULT_COLS = ["messages", "replies", "reactions_given", "reactions_received", "mentions_answered", "dms_answered", "response_median_min", "active_minutes", "keystrokes", "clicks", "files", "huddle_minutes", "channels_active", "last_active"];

const ColumnPicker = ({ cols, toggle }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="tc-popwrap">
      <button className="tc-btn tc-btn--outline sm" onClick={() => setOpen((o) => !o)} data-testid="columns-button">Columns ({cols.length})</button>
      {open && (
        <div className="tc-pop tc-pop--cols tc-fade-in" data-testid="columns-panel">
          {COLUMNS.map(([k, label]) => (
            <label key={k} className="tc-pop__check"><input type="checkbox" checked={cols.includes(k)} onChange={() => toggle(k)} data-testid={`column-toggle-${k}`} /> {label}</label>
          ))}
        </div>
      )}
    </div>
  );
};
